import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Trophy } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyTeamsCard = () => {
  const { user, refreshUser } = useAuth();
  const [myTeams, setMyTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState({ open: false, teamId: null, teamName: '' });

  useEffect(() => {
    if (user) {
      loadMyTeams();
    }
  }, [user]);

  const loadMyTeams = async () => {
    if (!user?.teams || user.teams.length === 0) {
      setMyTeams([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/teams`);
      // Filter to only show teams the user is a member of
      const userTeams = response.data.filter(team => user.teams.includes(team.id));
      setMyTeams(userTeams);
    } catch (error) {
      console.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const leaveTeam = async (teamId, teamName) => {
    setConfirmLeave({ open: true, teamId, teamName });
  };

  const handleConfirmLeave = async () => {
    const { teamId } = confirmLeave;
    try {
      const response = await axios.post(`${API}/teams/${teamId}/leave`);
      toast.success(response.data.message);
      await refreshUser(); // Refresh user data to update teams list
      loadMyTeams(); // Refresh the teams list
    } catch (error) {
      console.error('Leave team error:', error);
      toast.error(error.response?.data?.detail || 'Failed to leave team');
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
        <CardContent className="py-8 text-center">
          <div className="animate-pulse text-yellow-400">Loading your teams...</div>
        </CardContent>
      </Card>
    );
  }

  if (myTeams.length === 0) {
    return null; // Don't show the section if no teams
  }

  return (
    <>
      <ConfirmDialog
        open={confirmLeave.open}
        onOpenChange={(open) => setConfirmLeave({ ...confirmLeave, open })}
        title="Leave Team?"
        description={`Are you sure you want to leave ${confirmLeave.teamName}?`}
        confirmText="Leave Team"
        cancelText="Cancel"
        onConfirm={handleConfirmLeave}
        variant="destructive"
      />
    <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
      <CardHeader>
        <CardTitle className="text-white">My Teams</CardTitle>
        <CardDescription>Teams you are currently a member of</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {myTeams.map((team) => (
          <div 
            key={team.id}
            className="p-4 bg-blue-900/70 border border-blue-700 rounded-lg"
          >
            <div className="flex justify-between items-start">
              <div className="flex items-start space-x-4 flex-1">
                {/* Club Icon */}
                {team.club_icon ? (
                  <img 
                    src={`${API}/teams/${team.id}/club-icon?t=${Date.now()}`}
                    alt="Club icon"
                    className="w-16 h-16 rounded-lg object-cover border-2 border-yellow-500 flex-shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-yellow-500 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-8 h-8 text-blue-900" />
                  </div>
                )}
                
                {/* Team Info */}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">{team.name}</h3>
                  <p className="text-sky-300 text-sm">{team.club_name}</p>
                  {team.sport && (
                    <p className="text-gray-400 text-sm mt-1">Sport: {team.sport}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                    <span>👥 {team.players?.length || 0} members</span>
                    {team.coach_name && (
                      <span>👨‍🏫 Coach: {team.coach_name}</span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                onClick={() => leaveTeam(team.id, team.name)}
                variant="outline"
                size="sm"
                className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
              >
                Leave Team
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
    </>
  );
};

export default MyTeamsCard;
